import { ComponentElementCondition, ContainerElement } from '../../types/common/item-definition-types';
import { ItemValueKey } from '../../config/item-value-key';
import { ValueOriginType } from '../../enums/common/value-origin-type';
import { DetailConditionOperator } from '../../enums/common/detail-condition-operator';
import { ApplicantType } from '../modules/invitation/types';

const APPLICANT_TYPE_KEY = 'applicantType';

/** 入居者のみの申込みで出さないコンテナ。電気・ガスの需給契約に関する入力は契約者が行う */
const CONTRACTOR_ONLY_CONTAINER_IDS: string[] = ['SelectBasicPlan', ItemValueKey.PaymentMethodSelection];

/** 「お引越し情報」のうち入居者のみの申込みでも出す項目。それ以外は電気・ガスの項目 */
const MOVE_INFO_CONTAINER_ID = 'MoveInfo';
const OCCUPANT_MOVE_INFO_COMPONENT_IDS: string[] = [ItemValueKey.OccupyScheduledDate];

/** 契約者のみの申込みで出さない項目。入居予定日・生年月日・性別は入居者の属性 */
const OCCUPANT_ONLY_COMPONENT_IDS: string[] = [
  ItemValueKey.OccupyScheduledDate,
  ItemValueKey.CustomerBirthDay,
  ItemValueKey.CustomerGender
];

const notApplicantType = (applicantType: ApplicantType): ComponentElementCondition => ({
  detailConditions: [
    {
      valueType: ValueOriginType.ApplicationParams,
      valueKey: APPLICANT_TYPE_KEY,
      operator: DetailConditionOperator.NotEqual,
      targetValues: [{ valueType: ValueOriginType.Fixed, valueKey: applicantType }]
    }
  ]
});

const withCondition = <T extends { isDisplayCondition?: Array<ComponentElementCondition> }>(
  element: T,
  condition: ComponentElementCondition
): T => ({
  ...element,
  isDisplayCondition: [...(element.isDisplayCondition || []), condition]
});

/**
 * 申込者種別（契約者 / 入居者 / 兼任）で入力項目を出し分けるため、item-definitions の表示条件に
 * applicantType の条件を足したものを返す。
 *
 * 表示条件で隠すので、隠れた項目は ComponentElement が `${valueKey}IsDisplayed = false` を立てて値を消す。
 * これにより必須チェック（ableGoToNextPageConditionDefinition）と確認画面にも同じ出し分けが効く。
 * item-definitions はテナント設定（_env/cde）なので書き換えず、v2 側で上乗せする
 */
export const applyApplicantTypeConditions = (containers: Array<ContainerElement>): Array<ContainerElement> =>
  containers.map(container => {
    const target = CONTRACTOR_ONLY_CONTAINER_IDS.includes(container.id)
      ? withCondition(container, notApplicantType(ApplicantType.Occupant))
      : container;

    if (!target.components) {
      return target;
    }

    return {
      ...target,
      components: target.components.map(component => {
        let result = component;
        if (container.id === MOVE_INFO_CONTAINER_ID && !OCCUPANT_MOVE_INFO_COMPONENT_IDS.includes(component.id)) {
          result = withCondition(result, notApplicantType(ApplicantType.Occupant));
        }
        if (OCCUPANT_ONLY_COMPONENT_IDS.includes(component.id)) {
          result = withCondition(result, notApplicantType(ApplicantType.Contractor));
        }
        return result;
      })
    };
  });
